import { GroupBase } from 'react-select';
import { ProjectId, UserStatus, UserType, Team } from 'api/models';
import { MemberType, User as UserOptionType } from 'api/models/core-types';
import { Field } from 'api/tasks';
import { TimeoffResponse } from 'api/timeoff/types';
import { CustomOption } from 'components/atoms/select';
import { Option } from 'components/molecules/squad-picker/types';
import { UserOption } from 'utils/project-fields/types';

export const AssigneePickerObjectTypes = {
  NotInProject: 'NotInProject',
  InProject: 'InProject',
  Team: 'Team',
  Company: 'Company',
  SameRole: 'SameRole',
};

export type AssigneePickerObjectType =
  (typeof AssigneePickerObjectTypes)[keyof typeof AssigneePickerObjectTypes];

export const AssigneePickerFilter = {
  All: 'ALL',
  NativePartnerUsersAndTeams: 'NATIVE_PARTNER_USERS_AND_TEAMS',
  ExternalUsers: 'EXTERNAL_USERS',
  ExternalAndClientPartnerUsers: 'EXTERNAL_AND_CLIENT_PARTNER_USERS',
  InProjectUsersAndTeams: 'IN_PROJECT_USERS_AND_TEAMS',
  NotInProjectUsersAndTeams: 'NOT_IN_PROJECT_USERS_AND_TEAMS',
  InProjectUsers: 'IN_PROJECT_USERS',
  NotInProjectUsers: 'NOT_IN_PROJECT_USERS',
  NativeUsersAndTeams: 'NATIVE_USERS_AND_TEAMS',
  NativeUsers: 'NATIVE_USERS',
  Teams: 'TEAMS',
  UnResolvedPlaceholders: 'UNRESOLVED_PLACEHOLDERS',
  PlaceholderResolutions: 'PLACEHOLDER_RESOLUTIONS',
  CompanyUsers: 'COMPANY_USERS',
  UserFields: 'USER_FIELDS',
  UserFieldsWithMembersPlaceholder: 'USER_FIELDS_WITH_MEMBERS_PLACEHOLDER',
  SprintUsers: 'SPRINT_USERS',
} as const;

export type AssigneePickerFilterType =
  (typeof AssigneePickerFilter)[keyof typeof AssigneePickerFilter];

// 'raw' - returns just userId (default)
// 'prefixed' - returns user_${userId}
// 'simple' - returns user${userId}
// 'string' - returns userId as string
export type OptionValueFormat = 'raw' | 'prefixed' | 'simple' | 'string';

export const AssigneePickerOptionGroupId = {
  Assignees: 'assignees',
  Users: 'users',
  Teams: 'teams',
  UsersNotInProject: 'users_not_in_project',
  TeamMembers: 'team_members',
  Roles: 'roles',
  OtherTeamMembers: 'other_team_members',
  Partners: 'partners',
  PlaceholderResolutions: 'placeholder_resolutions',
  UserFields: 'user_fields',
  UserFieldsWithMembersPlaceholder: 'user_fields_with_members_placeholder',
  SprintUsers: 'sprint_users',
  NonSprintUsers: 'non_sprint_users',
} as const;

export type AssigneePickerOptionGroupIdType =
  | (typeof AssigneePickerOptionGroupId)[keyof typeof AssigneePickerOptionGroupId]
  | undefined;

export interface AssigneePickerOptionsType
  extends CustomOption<{
    id: number;
    type: 'team' | 'user' | 'company' | 'fields';
    teamId?: number;
    roleId?: number;
    timeOffDetails?: TimeoffResponse[];
    holidayCalendarId?: number;
    teamType?: MemberType;
    userGroup?: 'inProject' | 'notInProject';
  }> {
  value: string | number;
  userStatus?: UserStatus;
}

export type PlainAssigneePickerOptions = AssigneePickerOptionsType;

export interface AssigneePickerOptionGroup
  extends GroupBase<AssigneePickerOptionsType> {
  readonly id: AssigneePickerOptionGroupIdType;
  readonly type?: string;
  readonly data?: any;
}

export type AssigneeOptionsReturnType<G extends boolean> = {
  options: G extends true
    ? AssigneePickerOptionGroup[]
    : AssigneePickerOptionsType[];
};

export type AssigneeSearchResult<G extends boolean> = {
  handleLoadOptions: (
    inputValue: string,
  ) => Promise<
    G extends true ? AssigneePickerOptionGroup[] : AssigneePickerOptionsType[]
  >;
  initialOptions: G extends true
    ? AssigneePickerOptionGroup[]
    : AssigneePickerOptionsType[];
};

export type AsyncAssigneePickerPropsBase = {
  /*
  Option value format - This is to consolidate the different formats.
  'raw' - returns just userId (default)
  'prefixed' - returns user_${userId}
  'simple' - returns user${userId}
  @TODO Logesh - In future we should stick to a single format. Since the change is large, we are using this workaround
  */
  optionValueFormat?: OptionValueFormat;
  projectId?: ProjectId;
  filter?: AssigneePickerFilterType;
  includeDeactivatedUsers?: boolean;
  initialSearchString?: string;
  isPrivate?: boolean;
  includeRestricedPartnersMembers?: boolean;
  userType?: UserType[];
  userStatus?: UserStatus[];
  taskStartDateTime?: string; // ISO Date format as string (e.g., "2023-09-01")
  taskEndDateTime?: string; // ISO Date format as string (e.g., "2023-09-13")
  excludeUserIds?: string[];
  fetchUnResolvedPlaceholdersOnly?: boolean;
  placeholder?: Team;
  companyId?: number;
  selected?: Option[];
  partners?: UserOption[];
  showSelectedUsersInGroup?: boolean;
  customOptions?: UserOptionType[];
  peopleCurrentUserHaveAccessTo?: number[];
  fieldData?: Field[];
  sprintSquad?: string[];
  excludedUserTypes?: UserType[];
  overrideTeams?: Team[];
  groupLabelOverrides?: Partial<
    Record<NonNullable<AssigneePickerOptionGroupIdType>, string>
  >;
};

export type AsyncAssigneePickerPropsType<G extends boolean = boolean> =
  AsyncAssigneePickerPropsBase & {
    groupOptions: G;
  };
